/**
 * The arcade ledger client — the storage seam under the pure HighScoreTable
 * rules (owner slice 2026-07-29; see HighScoreTable.ts for the scope record).
 *
 * TWO BACKENDS, ONE BEHAVIOUR:
 * - The deployed site talks to same-origin `/api/scores` (a small KV endpoint
 *   on the game Worker) — the SHARED family ledger, cross-device.
 * - Localhost / the LAN vite server has no worker, so every call falls back to
 *   a per-browser localStorage ledger — the classic per-cabinet arcade list.
 * The fallback is transparent and per-call: a submit that cannot reach the
 * network still lands locally, and the local mirror is ALWAYS updated (via
 * mergeTables) so the attract screen has something to show offline.
 *
 * Storage keys are namespaced per seed: mixing Simple (~55k) and ranked
 * (~540k) scores in one table would bury every kid entry — the seed id IS the
 * table key, and the Simple e2e QA pin (`trash-panic:best` must stay null
 * after a Simple run) is respected by construction because this module never
 * touches the best/history keys.
 *
 * Dependency-injected (fetch + storage + clock) so bun can unit-test the whole
 * seam with fakes — the browser wiring in boot/shell passes the real ones.
 */
import {
  type HighScoreEntry,
  TABLE_DISPLAY_SIZE,
  insertEntry,
  isSaneScore,
  mergeTables,
  normalizeTable,
  qualifies,
  sanitizeInitials,
} from "./HighScoreTable.js";

const LOCAL_PREFIX = "trash-panic:arcade:";
const INITIALS_KEY = "trash-panic:arcade-initials";
/** Same-origin endpoint; the Worker routes it ahead of the SPA fallback. */
const API_PATH = "/api/scores";
/** A slow ledger must never hold the results screen hostage. */
const FETCH_TIMEOUT_MS = 3000;

export type LedgerSource = "shared" | "local";

export interface LedgerView {
  entries: HighScoreEntry[];
  /** Which backend answered — the UI says "FAMILY BOARD" vs "THIS MACHINE". */
  source: LedgerSource;
}

interface ArcadeDeps {
  fetchImpl?: typeof fetch | null;
  storage?: Pick<Storage, "getItem" | "setItem"> | null;
  now?: () => number;
}

/** The five known seed ids get tables; anything else is refused client-side too. */
const SEED_ID_PATTERN = /^[a-z0-9-]{1,48}$/;

export class ArcadeLedger {
  private readonly fetchImpl: typeof fetch | null;
  private readonly storage: Pick<Storage, "getItem" | "setItem"> | null;
  private readonly now: () => number;

  constructor(deps: ArcadeDeps = {}) {
    this.fetchImpl = deps.fetchImpl === undefined ? (typeof fetch === "function" ? fetch.bind(globalThis) : null) : deps.fetchImpl;
    this.storage = deps.storage === undefined ? safeLocalStorage() : deps.storage;
    this.now = deps.now ?? (() => Date.now());
  }

  /** Top table for a seed: shared when reachable, local otherwise. */
  async load(seedId: string): Promise<LedgerView> {
    if (!SEED_ID_PATTERN.test(seedId)) return { entries: [], source: "local" };
    const remote = await this.remoteGet(seedId);
    if (remote) {
      // Mirror the shared table locally so offline attract screens stay warm.
      this.writeLocal(seedId, mergeTables(remote, this.readLocal(seedId)));
      return { entries: remote, source: "shared" };
    }
    return { entries: this.readLocal(seedId), source: "local" };
  }

  /** The "enter your initials" gate, against whichever backend answers. */
  async shouldPrompt(seedId: string, score: number): Promise<boolean> {
    if (!isSaneScore(score)) return false;
    const { entries } = await this.load(seedId);
    return qualifies(entries, score);
  }

  /**
   * Record an entry. Local mirror is written unconditionally; the shared POST
   * is best-effort. Returns the freshest table plus the entry's display rank.
   */
  async submit(seedId: string, initials: string, score: number): Promise<LedgerView & { rank: number | null; entry: HighScoreEntry | null }> {
    if (!SEED_ID_PATTERN.test(seedId) || !isSaneScore(score)) {
      return { entries: this.readLocal(seedId), source: "local", rank: null, entry: null };
    }
    const entry: HighScoreEntry = { initials: sanitizeInitials(initials), score: Math.floor(score), ts: this.now() };
    this.rememberInitials(entry.initials);

    // Local first — a crash or dead network after this line still kept the score.
    const local = insertEntry(this.readLocal(seedId), entry);
    this.writeLocal(seedId, local.table);

    const remote = await this.remotePost(seedId, entry);
    if (remote) {
      this.writeLocal(seedId, mergeTables(remote, local.table));
      const index = remote.findIndex((row) => row.initials === entry.initials && row.score === entry.score && row.ts === entry.ts);
      return { entries: remote, source: "shared", rank: index >= 0 && index < TABLE_DISPLAY_SIZE ? index + 1 : null, entry };
    }
    return { entries: local.table, source: "local", rank: local.rank, entry };
  }

  /** Last-used initials, so a returning kid gets their letters pre-loaded. */
  lastInitials(): string {
    const raw = this.storage?.getItem(INITIALS_KEY);
    return raw ? sanitizeInitials(raw) : "AAA";
  }

  private rememberInitials(initials: string): void {
    try { this.storage?.setItem(INITIALS_KEY, initials); } catch { /* quota — non-fatal */ }
  }

  private readLocal(seedId: string): HighScoreEntry[] {
    try {
      const raw = this.storage?.getItem(LOCAL_PREFIX + seedId);
      return raw ? normalizeTable(JSON.parse(raw)) : [];
    } catch { return []; }
  }

  private writeLocal(seedId: string, table: HighScoreEntry[]): void {
    try { this.storage?.setItem(LOCAL_PREFIX + seedId, JSON.stringify(table)); } catch { /* quota — non-fatal */ }
  }

  private async remoteGet(seedId: string): Promise<HighScoreEntry[] | null> {
    if (!this.fetchImpl) return null;
    try {
      const response = await this.fetchImpl(`${API_PATH}?seed=${encodeURIComponent(seedId)}`, { method: "GET", signal: this.timeoutSignal() });
      if (!response.ok) return null;
      const body = await response.json() as { entries?: unknown };
      return normalizeTable(body.entries);
    } catch { return null; }
  }

  private async remotePost(seedId: string, entry: HighScoreEntry): Promise<HighScoreEntry[] | null> {
    if (!this.fetchImpl) return null;
    try {
      const response = await this.fetchImpl(API_PATH, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ seed: seedId, ...entry }),
        signal: this.timeoutSignal(),
      });
      if (!response.ok) return null;
      const body = await response.json() as { entries?: unknown };
      return normalizeTable(body.entries);
    } catch { return null; }
  }

  /**
   * A real AbortSignal, not a Promise.race: the race left the underlying
   * request running and did not cover a hung `response.json()` body read
   * (review finding) — an aborted signal cancels both. Older environments
   * without AbortSignal.timeout simply get no timeout, which degrades to the
   * fetch's own failure handling.
   */
  private timeoutSignal(): AbortSignal | undefined {
    return typeof AbortSignal !== "undefined" && "timeout" in AbortSignal
      ? AbortSignal.timeout(FETCH_TIMEOUT_MS)
      : undefined;
  }
}

/** localStorage access itself THROWS when a browser blocks site data — a
 * blocked-storage browser must get the no-persistence ledger, not a blank
 * boot (review finding). */
function safeLocalStorage(): Pick<Storage, "getItem" | "setItem"> | null {
  try {
    return typeof localStorage === "undefined" ? null : localStorage;
  } catch { return null; }
}
